/**
 * "What can I actually afford?" (spec §7): the maximum-spending solve,
 * displayed as its own card with one-click apply.
 */
import { useMemo } from 'react';
import type { Plan } from '../model/types';
import { solveMaxSpending } from '../engine/solver';
import { fmtUsd } from './format';

interface Props {
  plan: Plan;
  onChange: (updated: Plan) => void;
}

export function AffordCard({ plan, onChange }: Props) {
  const solved = useMemo(() => solveMaxSpending(plan), [plan]);

  const currentBudget =
    plan.budget.mode === 'simple' ? plan.budget.annualAmount ?? 0
    : plan.budget.mode === 'staircase' ? plan.budget.baseAnnualAmount ?? 0
    : null;
  const max = solved.annualSpendingToday;
  const delta = currentBudget != null ? max - currentBudget : null;

  return (
    <section className="card" aria-label="Maximum sustainable spending">
      <h2 className="section-title">
        What can I actually afford?
        <span className="note">constant real spending, solved to last exactly to the end of the plan</span>
      </h2>
      <div className="hero-row">
        <div className="stat">
          <div className="label">Maximum sustainable spending</div>
          <div className="value">{fmtUsd(max)}<span style={{ fontSize: '0.5em', color: 'var(--ink-muted)' }}> /yr</span></div>
          <div className="context">today's dollars · under the current assumptions and strategy</div>
        </div>
        {delta != null && (
          <div className="stat">
            <div className="label">vs. your current budget</div>
            <div className={`value${delta >= 0 ? '' : ' negative'}`}>
              {delta >= 0 ? '+' : '−'}{fmtUsd(Math.abs(delta))}
            </div>
            <div className="context">
              {delta >= 0
                ? 'room to spare — you could spend this much more every year'
                : 'your current budget overshoots what the plan sustains'}
            </div>
          </div>
        )}
      </div>
      {plan.budget.mode === 'simple' && (
        <button className="btn"
          onClick={() => {
            const next = structuredClone(plan);
            next.budget.annualAmount = max;
            next.budget.retirementSpendingMultiplier = 1;
            onChange(next);
          }}>
          Use {fmtUsd(max)}/yr as my budget
        </button>
      )}
      <p className="disclaimer" style={{ marginTop: 'var(--space-3)' }}>
        A deterministic solve under the active assumption profile — spending right at this level
        leaves no cushion for bad markets. The Monte Carlo version (with a success-probability
        target) arrives in a later phase.
      </p>
    </section>
  );
}
